import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import { Action } from '../../domain/infra/remote';
import { FrontRemoteHttpService } from '../../services/http/remoteHttp.service';
import { FrontRemoteRequest } from '../../domain/infra/remote';
import { IziviaLoggerService } from '../../services/common/iziviaLogger.service';
import { RemoteResponse } from '../../domain/infra/remote';
import { RemoteStatus } from '../../domain/infra/remote';
import {EmptyError, interval, noop, Observable, Subscription} from 'rxjs';
import {filter, first, startWith, switchMap, take, tap} from 'rxjs/operators';
import {IziviaToastOptions, IziviaToastService} from '../../services/common/iziviaToast.service';
import {REMOTE_REQUEST_PERIOD_IN_MS} from '../../services/settings/settings.keys';
import {RemoteInfoChargeRecord, RemoteInfoMedia} from '../../domain/chargeRecord/remoteInfoChargeRecord';

@Component({
    selector: 'remote[request][action]',
    templateUrl: './remote.component.html',
    styleUrls: ['./remote.component.scss'],
})
export class RemoteComponent implements OnChanges, OnDestroy {
    @Input() action: Action;
    @Input() request: RemoteInfoMedia | RemoteInfoChargeRecord | FrontRemoteRequest;
    @Input() maximumNumberOfRetry = 2;

    @Output() remoteResponse: EventEmitter<RemoteResponse> = new EventEmitter();

    // dirty hack to enable type-safety to the view
    Action = Action;

    waitRemoteRequest: boolean;
    waitUserConnectVehicle: boolean;
    private pollingSubscription: Subscription;

    constructor(private readonly remoteHttpService: FrontRemoteHttpService,
        private readonly toastService: IziviaToastService,
        private readonly loggerService: IziviaLoggerService,
        @Inject(REMOTE_REQUEST_PERIOD_IN_MS) private readonly remoteRequestPeriodInMs) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.request) {
            this.unsubscribePollingSubscriptionIfPresent();
            this.sendRemoteAction();
        }
    }

    /**
     * Send a remote start or stop
     */
    private sendRemoteAction() {
        let remoteResponse$: Observable<RemoteResponse>;
        if (!this.request) {
            return;
        }

        this.loggerService.debug(`[sendRemoteAction] Action ${this.action}:`, this.request);

        switch (this.action) {
            case Action.START:
                if (this.request instanceof RemoteInfoMedia) {
                    remoteResponse$ = this.remoteHttpService.sendRemoteAction(
                        Action.START,
                        this.request.stationRef,
                        this.request.chargePointRef,
                        this.request.plugRef,
                        this.request.mediaCode);
                } else {
                    remoteResponse$ = this.remoteHttpService.sendRemoteActionWithRequest(
                        Action.START, <FrontRemoteRequest>this.request);
                }
                break;
            case Action.STOP:
                if (this.request instanceof RemoteInfoChargeRecord) {
                    remoteResponse$ = this.remoteHttpService.remoteStopChargeRecord(this.request.chargeRecordId);
                }
                break;
        }

        if (!remoteResponse$) {
            this.loggerService.warning('[sendRemoteAction] No remote request built', this.action, this.request);
            return;
        }

        this.waitRemoteRequest = true;
        remoteResponse$
            .pipe(
                tap(response => this.handleRemoteStatus(response, true))
            )
            .subscribe(
                () => noop(),
                error => this.remoteError(error)
            );
    }

    /**
     * Call server for get the new state of remote (if there is a new state)
     */

    pollingRemoteStatus(remoteRef: string) {
        let count: number = 0;
        this.pollingSubscription = interval(this.remoteRequestPeriodInMs) // Every 5 seconds
            .pipe(
                startWith(0),
                take(24), // Times out after 2m
                tap(() => {
                    if (count === 6 || count === 12) { // At 30 seconds or 1 minute, send toaster to reassure the client
                        this.toastService.showSuccess('remote.waitingForAnswer');
                    }
                    count++;
                }),
                switchMap(() => this.remoteHttpService.getRemoteByRemoteRef(this.action, remoteRef)),
                filter((remoteResponse: RemoteResponse) => this.isStatusDone(remoteResponse) || this.isStatusError(remoteResponse)),
                first(),
            ).subscribe(
                remoteResponse => this.handleRemoteStatus(remoteResponse),
                error => this.remoteError(error)
            );
    }

    ngOnDestroy(): void {
        this.unsubscribePollingSubscriptionIfPresent();
    }

    /**
     * check the remote response,
     * if param shouldCreateTimer is true then create a timer that check the remote status and, wait for a status done or error
     * @param response of remote
     * @param shouldCreatePolling if true create a timer for check remote status
     */
    private handleRemoteStatus(response: RemoteResponse, shouldCreatePolling?: boolean): void {
        this.loggerService.debug(`[handleRemoteStatus] Status: ${response.status}`);

        if (this.isStatusDone(response)) {
            this.remoteResponse.emit(response);
            this.unsubscribePollingSubscriptionIfPresent();
            this.waitUserConnectVehicle = false;
        } else if (this.isStatusError(response)) {
            const maximumRetryReached = this.isMaximumRetryReached(response);
            const errorLabel = maximumRetryReached ? 'remote.error.definitiveError' : `remote.error.${response.errorReason}`;
            this.remoteError(errorLabel, false);
            this.remoteResponse.emit(response);
        } else if (shouldCreatePolling && !!response.remoteRef) {
            this.waitRemoteRequest = false;
            this.waitUserConnectVehicle = true;
            this.unsubscribePollingSubscriptionIfPresent();
            this.pollingRemoteStatus(response.remoteRef);
        } else {
            this.unsubscribePollingSubscriptionIfPresent();
            this.remoteResponse.emit(response);
        }
    }

    private isStatusError(response: RemoteResponse): boolean {
        return response.status === RemoteStatus.ERROR || response.status === RemoteStatus.TIMEOUT;
    }

    private isStatusDone(response: RemoteResponse): boolean {
        return response.status === RemoteStatus.DONE && !!response.chargeRef;
    }

    /**
     * @return true if response.numberOfRetry is equal of superior to maximumNumberOfRetry.
     * The first failed attempt numberOfRetry is 0. To show definitive message
     * on 3rd failed attempts maximumNumberOfRetry needs to be set to 2.
     */
    private isMaximumRetryReached(response: RemoteResponse): boolean {
        return response.numberOfRetry && response.numberOfRetry >= this.maximumNumberOfRetry;
    }

    private remoteError(serverError: { error: { labelKey: string } } | string, emit: boolean = true) {
        if (serverError instanceof EmptyError) {
            // rxjs timeout: 2min pulling
            this.remoteResponse.emit({status: RemoteStatus.TIMEOUT, remoteRef: null});
        } else {
            this.loggerService.debug(`[remoteError] Reason:`, serverError);
            const toastOptions = new IziviaToastOptions();
            toastOptions.duration = 'long';
            toastOptions.header = `remote.${this.action.toLowerCase()}.error`;

            this.toastService.showServerErrorOrFallback(serverError, `remote.${this.action.toLowerCase()}.error`, toastOptions);
            this.unsubscribePollingSubscriptionIfPresent();
            this.waitRemoteRequest = false;
            this.waitUserConnectVehicle = false;

            if (emit) {
                this.remoteResponse.emit({status: RemoteStatus.ERROR, remoteRef: null});
            }
        }
    }


    private unsubscribePollingSubscriptionIfPresent() {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
        }
    }
}
